import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { useAuth } from "@/lib/auth";

const TONE = { expected: "text-sky-200 border-sky-400/40", posted: "text-emerald-300 border-emerald-400/40", cancelled: "text-slate-400 border-white/15" };

/** Resident Financial card: 'Coming up' line for Expected paychecks from labor invoices. */
export function ComingUp({ residentId, tick }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { finApi.get("/expected-paychecks", { resident_id: residentId }).then((d) => setRows(d.entries || [])).catch(() => setRows([])); }, [residentId, tick]);
  if (!rows.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-1" data-testid="financial-coming-up">
      {rows.map((e) => {
        const ready = e.pay_date && e.pay_date <= today;
        return <div key={e.id} data-testid={`financial-coming-up-${e.id}`} className={`text-xs px-2 py-1 rounded-sm border ${ready ? "border-amber-400/50 bg-amber-500/10 text-amber-200" : "border-white/10 text-nex-muted"}`}>
          {ready ? "Ready to log · " : "Coming up · "}Expected {money(e.amount)} on {e.pay_date} from {e.vendor_name || "invoice"} — open Log paycheck to confirm</div>;
      })}
    </div>
  );
}

/** Invoice detail: each resident's expected paycheck and its status, plus the SA backfill button for older invoices. */
export function InvoiceExpected({ inv }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => finApi.get(`/invoices/${inv.id}/expected-paychecks`).then(setD).catch(() => setD(null));
  useEffect(() => { if (inv.kind === "resident_labor") load(); }, [inv.id, inv.status, inv.archived]); // eslint-disable-line react-hooks/exhaustive-deps
  if (inv.kind !== "resident_labor" || !d) return null;
  const awaiting = !["paid", "voided", "draft"].includes(inv.status) && !inv.archived;
  const create = async () => {
    if (!window.confirm("This checks each resident for an existing paycheck in this period first, so no one is paid twice. Create expected paychecks?")) return;
    try { const r = await finApi.post(`/invoices/${inv.id}/expected-paychecks`); toast.success(`${r.created} expected paycheck(s) created`); load(); } catch (e) { toast.error(finErr(e)); }
  };
  return (
    <div className="space-y-1.5 p-3 border border-white/10 rounded-sm" data-testid="invoice-expected">
      <div className="flex items-center gap-2"><span className="nds-label-dim text-[9px]">Resident paychecks · {d.posted} of {d.total} posted</span>
        {user?.role === "super_admin" && awaiting && <button type="button" onClick={create} data-testid="invoice-expected-create" className="ml-auto font-mono text-[10px] uppercase text-nex-gold hover:underline">Create expected paychecks</button>}</div>
      {d.entries.map((e) => <div key={e.id} className="text-xs flex flex-wrap items-center gap-2" data-testid={`invoice-expected-${e.resident_id}`}>
        <span className="text-nex-silver">{e.resident_name}</span><span className="font-mono">{money(e.posted_amount || e.amount)}</span>
        <span className={`px-1.5 rounded-full border text-[10px] uppercase ${TONE[e.status]}`}>{e.status}</span>
        {e.warning && <span className="text-amber-300 text-[11px]" data-testid={`invoice-expected-warn-${e.resident_id}`}>{e.warning}</span>}</div>)}
      {d.skipped.map((s) => <div key={s.resident_id} className="text-[11px] text-amber-200" data-testid={`invoice-expected-skipped-${s.resident_id}`}>{s.resident_name} · {s.reason}</div>)}
    </div>
  );
}
